import { DocumentReference } from "firebase/firestore";

/**
 * Safely extracts the ID from a reference or object
 * 
 * @param ref - Firebase document reference or object with ID
 * @returns string ID or empty string
 */
export function getRefId(ref: any): string {
  if (!ref) return '';
  
  // If it's a string, return it directly
  if (typeof ref === 'string') return ref;
  
  // If it has an id property directly
  if (ref.id) return ref.id;
  
  // If it's a Firebase reference with path
  if (ref.path) {
    const parts = ref.path.split('/');
    return parts[parts.length - 1];
  }
  
  return '';
}

/**
 * Processes a Firestore document reference into a usable object
 * If `keepFullData` is true and the reference has data, return the full data object
 * 
 * @param ref - Firebase document reference or data object
 * @param keepFullData - Whether to keep all data properties from the object
 * @returns Processed reference with at minimum an id property
 */
export function processReference(ref: any, keepFullData = false): any {
  if (!ref) return null;
  
  // If the reference is null or undefined
  if (ref === null || ref === undefined) return null;
  
  // If we just want the ID from a reference
  if (!keepFullData) {
    // If it's a simple string ID
    if (typeof ref === 'string') return { id: ref };
    
    // If it's a Firebase doc reference with an id property
    if (ref.id) return { id: ref.id };
    
    // If it's a Firebase reference with path
    if (ref.path) {
      const parts = ref.path.split('/');
      const id = parts[parts.length - 1];
      return { id };
    }
    
    // For other objects, return as is
    return ref;
  }
  
  // If we're keeping the full data object (used for user references)
  // First check if we already have a data object with fields
  if (typeof ref === 'object' && (ref.firstName || ref.lastName || ref.email || ref.displayName)) {
    return ref;
  }
  
  // For Firebase references that have a data method
  if (ref.data && typeof ref.data === 'function') {
    const data = ref.data();
    if (data) {
      return {
        ...data,
        id: ref.id
      };
    }
  }
  
  // If it's a Firebase reference with just an id
  if (ref.id) {
    return { id: ref.id };
  }
  
  // If it's a Firebase reference with path
  if (ref.path) {
    const parts = ref.path.split('/');
    const id = parts[parts.length - 1];
    return { id };
  }
  
  // Default fallback
  return ref;
}
